import { NextRequest } from 'next/server'
import { pathToRegexp } from 'path-to-regexp'
import { log } from '../util/log'
export const BASE_PATH = '/dashboard'

export type PathName =
  | '/'
  | '/account'
  | '/producttegoed'
  | '/payment-validate/:jobId'
  | '/statistieken'
  | '/vacature/:id'
  | '/vacature/:id/credits'
  | '/vacature/:id/product'
  | '/vacature/:id/reporting'
  | '/vacature/:id/informatie'
  | '/vacature/:id/resultaat'
  | '/vacature/variabel/:id/:step'
  | '/vacatures'
  | '/vacatures/cart'
  | '/vacatures/cart/:status'
  | '/vacatures/:id'
  | '/webshop'
  | '/webshop/cart'
  | '/webshop/cart/:status'
  | '/webshop/configureren'
  | '/webshop/products/:id'

const getPathRegexp = (path: PathName): RegExp => pathToRegexp(path)

export type Route = {
  requiresAuthentication: boolean
  returnAfterLogin: boolean
  route: PathName
  regex: RegExp
}

const routes: Route[] = [
  {
    requiresAuthentication: true,
    returnAfterLogin: false,
    route: '/',
    regex: getPathRegexp('/'),
  },
  {
    requiresAuthentication: true,
    returnAfterLogin: false,
    route: '/account',
    regex: getPathRegexp('/account'),
  },
  {
    requiresAuthentication: true,
    returnAfterLogin: false,
    route: '/producttegoed',
    regex: getPathRegexp('/producttegoed'),
  },
  {
    requiresAuthentication: true,
    returnAfterLogin: false,
    route: '/payment-validate/:jobId',
    regex: getPathRegexp('/payment-validate/:jobId'),
  },
  {
    requiresAuthentication: true,
    returnAfterLogin: false,
    route: '/statistieken',
    regex: getPathRegexp('/statistieken'),
  },
  {
    requiresAuthentication: true,
    returnAfterLogin: true,
    route: '/vacature/:id',
    regex: getPathRegexp('/vacature/:id'),
  },
  {
    requiresAuthentication: true,
    returnAfterLogin: false,
    route: '/vacature/:id/credits',
    regex: getPathRegexp('/vacature/:id/credits'),
  },
  {
    requiresAuthentication: true,
    returnAfterLogin: false,
    route: '/vacature/:id/product',
    regex: getPathRegexp('/vacature/:id/product'),
  },
  {
    requiresAuthentication: true,
    returnAfterLogin: false,
    route: '/vacature/:id/reporting',
    regex: getPathRegexp('/vacature/:id/reporting'),
  },
  {
    requiresAuthentication: true,
    returnAfterLogin: false,
    route: '/vacature/:id/informatie',
    regex: getPathRegexp('/vacature/:id/informatie'),
  },
  {
    requiresAuthentication: true,
    returnAfterLogin: false,
    route: '/vacature/:id/resultaat',
    regex: getPathRegexp('/vacature/:id/resultaat'),
  },
  {
    requiresAuthentication: true,
    returnAfterLogin: false,
    route: '/vacature/variabel/:id/:step',
    regex: getPathRegexp('/vacature/variabel/:id/:step'),
  },
  {
    requiresAuthentication: true,
    returnAfterLogin: false,
    route: '/vacatures',
    regex: getPathRegexp('/vacatures'),
  },
  {
    requiresAuthentication: true,
    returnAfterLogin: false,
    route: '/vacatures/cart',
    regex: getPathRegexp('/vacatures/cart'),
  },
  {
    requiresAuthentication: true,
    returnAfterLogin: false,
    route: '/vacatures/cart/:status',
    regex: getPathRegexp('/vacatures/cart/:status'),
  },
  {
    requiresAuthentication: true,
    returnAfterLogin: false,
    route: '/vacatures/:id',
    regex: getPathRegexp('/vacatures/:id'),
  },
  {
    requiresAuthentication: false,
    returnAfterLogin: false,
    route: '/webshop',
    regex: getPathRegexp('/webshop'),
  },
  {
    requiresAuthentication: true,
    returnAfterLogin: true,
    route: '/webshop/cart',
    regex: getPathRegexp('/webshop/cart'),
  },
  {
    requiresAuthentication: true,
    returnAfterLogin: false,
    route: '/webshop/cart/:status',
    regex: getPathRegexp('/webshop/cart/:status'),
  },
  {
    requiresAuthentication: false,
    returnAfterLogin: false,
    route: '/webshop/configureren',
    regex: getPathRegexp('/webshop/configureren')
  },
  {
    requiresAuthentication: false,
    returnAfterLogin: false,
    route: '/webshop/products/:id',
    regex: getPathRegexp('/webshop/products/:id'),
  },
]

/**
 * Uses RegExp to match given pathname to a configured Route
 */
export const getRoute = (pathname: string): Route | null | undefined => routes.find(({ regex }) => regex.exec(pathname))

/**
 * Return login page url with (depending on the Route) state parameter instructing a redirect after successful login
 * @param {Request} req NextRequest
 * @returns URL
 */
export const getLoginUrl = (req: NextRequest): URL => {
  const returnAfterLogin = getRoute(req.nextUrl.pathname)?.returnAfterLogin
  const urlParams = returnAfterLogin ? `?state=${encodeURIComponent(btoa(`${BASE_PATH}${req.nextUrl.pathname}`))}` : ''
  
  return new URL(urlParams, process.env.SALES_FORCE_LOGIN_URL)
}

/**
 * Check if a user can access the route based on the route name, access token cookie and code search parameter
 * @param { NextRequest } req NextRequest (see Next.js docs)
 * @returns boolean
 */
export const isProtectedPath = (req: NextRequest): boolean => {
  const requiresAuthentication = getRoute(req.nextUrl.pathname)?.requiresAuthentication
  
  if (requiresAuthentication === undefined) {
    log.warn({}, `requiresAuthentication is not defined for route: ${getRoute(req.nextUrl.pathname)}`)
    return true
  }

  return requiresAuthentication
}
